/**
 * Change-significance gate — decides whether a turn's edits actually need the
 * project checks (typecheck / lint / test) run against them, so a tiny change
 * doesn't pay for a full verification pass.
 *
 * Two layers, both BOUNDED so verification integrity is never traded away:
 *
 *  1. Deterministic & always safe — if the agent only touched files no check
 *     can inspect (markdown/docs/images/…), there is nothing for typecheck or
 *     lint to catch, so verification is skipped no matter what.
 *  2. Model-judged & guarded — the agent can declare a change trivial (the
 *     {@link NOVERIFY_MARKER} completion signal). That judgement is honored
 *     ONLY inside a blast-radius guard: no deletions, no opaque commands, and a
 *     small, MEASURED line churn ({@link TRIVIAL_CHURN_LIMIT}). A large or
 *     cross-file edit is always re-checked even if the model waved it off.
 *
 * This module is pure (no fs / process) so the policy is unit-testable; the
 * callers measure churn at write-time and feed it in via {@link createChangeTracker}.
 */

/** A file the agent changed this turn. */
export interface ChangedFile {
  /** Repo-relative path the tool was called with. */
  readonly path: string;
  /**
   * Line edit-distance vs the pre-change file (LCS-based; see {@link lineChurn}).
   * `undefined` means it could not be measured — treated as "not trivially small".
   */
  readonly churn?: number | undefined;
  /** The file was deleted (cross-file risk: imports elsewhere may now dangle). */
  readonly deleted?: boolean | undefined;
  /** The file is brand new (created, not edited) — a fresh unit checks must vet. */
  readonly created?: boolean | undefined;
}

/** Everything mutated since the last verification, for the significance check. */
export interface ChangeSet {
  readonly files: readonly ChangedFile[];
  /**
   * A path-less mutating tool ran (e.g. `terminal_exec`) — its effect on the
   * tree is unknown, so the change can never be classified as trivial.
   */
  readonly opaqueMutation?: boolean | undefined;
}

/** The verdict: run the checks, or skip them — with a human-readable reason. */
export type VerifyNeed = { readonly verify: boolean; readonly reason: string };

/**
 * Max total line churn (across all changed code files) the agent may wave off
 * with the trivial marker. Above this the change is always verified.
 */
export const TRIVIAL_CHURN_LIMIT = 8;

/** Above this many lines a single file is never "trivial" — skip the LCS work. */
const CHURN_BAILOUT_LINES = 2000;

/** Extensions no project check (typecheck/lint/test/build) inspects. */
const NON_CODE_EXT = new Set([
  // prose / docs
  'md', 'mdx', 'markdown', 'rst', 'adoc', 'asciidoc', 'txt', 'text', 'rtf',
  // images
  'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'avif', 'ico', 'bmp', 'tiff',
  // media
  'mp4', 'mov', 'webm', 'mp3', 'wav', 'ogg',
  // fonts
  'woff', 'woff2', 'ttf', 'eot', 'otf',
  // misc artifacts that don't break a build (data files like csv/tsv are NOT
  // here on purpose — they are commonly test fixtures/parser inputs).
  'pdf', 'map',
]);

/** Whole filenames (lowercased) that can't affect any check. */
const NON_CODE_NAMES = new Set([
  'license', 'license.md', 'license.txt', 'copying', 'notice', 'authors',
  'contributors', 'changelog', 'changelog.md', 'history.md', 'readme', 'readme.md',
  '.gitignore', '.gitattributes', '.editorconfig', '.npmignore',
  '.prettierignore', '.eslintignore', '.nvmrc', 'codeowners',
]);

/**
 * True when the project's checks could plausibly inspect this file. Conservative
 * by design: anything not on the explicit non-code lists counts as checked, so
 * an unknown extension errs toward verifying.
 */
export function isCheckedFile(path: string): boolean {
  const base = (path.replace(/\\/g, '/').split('/').pop() ?? '').toLowerCase();
  if (!base) return true;
  if (NON_CODE_NAMES.has(base)) return false;
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return true; // extensionless (Dockerfile, Makefile, …) → verify
  return !NON_CODE_EXT.has(base.slice(dot + 1));
}

/** Build/tooling config whose tiny edits can break the WHOLE check run. */
const CONFIG_NAMES = new Set([
  'package.json', 'package-lock.json', 'pnpm-lock.yaml', 'yarn.lock', 'npm-shrinkwrap.json',
  'pnpm-workspace.yaml', 'turbo.json', 'nx.json', 'lerna.json', '.npmrc',
  'deno.json', 'deno.jsonc', 'bunfig.toml', 'gruntfile.js', 'gulpfile.js',
]);

/**
 * True for build/tooling config files. A one-line change to these (a `package.json`
 * field, a `tsconfig` flag, a lockfile, a CI workflow) can break typecheck/lint/test
 * for the entire project, so they are NEVER eligible for the model's trivial waiver
 * even though their churn is small.
 */
export function isConfigFile(path: string): boolean {
  const norm = path.replace(/\\/g, '/');
  const base = (norm.split('/').pop() ?? '').toLowerCase();
  if (CONFIG_NAMES.has(base)) return true;
  if (/^(tsconfig|jsconfig).*\.json[5c]?$/.test(base)) return true;
  if (/\.config\.(c|m)?[jt]s$/.test(base) || /\.config\.json$/.test(base)) return true;
  if (/^\.(eslintrc|prettierrc|babelrc|swcrc|stylelintrc)/.test(base)) return true;
  if (/\.lock(b)?$/.test(base)) return true; // bun.lockb, *.lock
  if (norm.includes('.github/workflows/')) return true;
  return false;
}

/**
 * Combine repeated changes to the SAME file within a turn. Churn is SUMMED (an
 * upper bound on the true edit-distance from the turn's start — so it can only
 * over-count, never wrongly clear the trivial guard); an unknown churn on either
 * side stays unknown; deleted/created stick.
 */
export function mergeChange(prev: ChangedFile | undefined, next: ChangedFile): ChangedFile {
  if (!prev) return next;
  const churn =
    prev.churn === undefined || next.churn === undefined ? undefined : prev.churn + next.churn;
  return {
    path: next.path,
    ...(churn !== undefined ? { churn } : {}),
    ...(prev.deleted || next.deleted ? { deleted: true } : {}),
    ...(prev.created || next.created ? { created: true } : {}),
  };
}

/**
 * Line edit-distance between two file versions: (lines removed) + (lines added),
 * computed from the longest common subsequence so a moved block correctly counts
 * as churn (not a free no-op). New files compare against an empty baseline.
 */
export function lineChurn(before: string, after: string): number {
  const a = before.length ? before.split('\n') : [];
  const b = after.length ? after.split('\n') : [];
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  // A file this large is never a "trivial" edit — don't pay for the O(n·m) DP.
  if (a.length > CHURN_BAILOUT_LINES || b.length > CHURN_BAILOUT_LINES) {
    return Math.max(a.length, b.length);
  }
  const m = b.length;
  let prev = new Array<number>(m + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const cur = new Array<number>(m + 1).fill(0);
    const ai = a[i - 1];
    for (let j = 1; j <= m; j++) {
      cur[j] = ai === b[j - 1] ? (prev[j - 1] ?? 0) + 1 : Math.max(prev[j] ?? 0, cur[j - 1] ?? 0);
    }
    prev = cur;
  }
  const lcs = prev[m] ?? 0;
  return a.length - lcs + (b.length - lcs);
}

function baseName(path: string): string {
  return path.replace(/\\/g, '/').split('/').pop() ?? path;
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * Decide whether a change needs the project checks. Order matters: an opaque
 * command forces verification; a docs-only change skips it; any code change is
 * verified unless the model waved it off AND it clears the blast-radius guard.
 */
export function classifyVerifyNeed(change: ChangeSet, opts: { modelWaived: boolean }): VerifyNeed {
  if (change.opaqueMutation) {
    return { verify: true, reason: 'a command was run (unknown effect)' };
  }
  const files = change.files;
  if (files.length === 0) return { verify: false, reason: 'no files changed' };

  // ANY deletion is checked first — even a "non-code" asset (logo.svg, a font, a
  // .json fixture) can be imported by code, so removing it can break the build.
  // This must precede the docs-only skip below, which only sees non-deletions.
  if (files.some((f) => f.deleted)) {
    return { verify: true, reason: 'a file was deleted' };
  }

  const checked = files.filter((f) => isCheckedFile(f.path));
  if (checked.length === 0) {
    const names = files.slice(0, 3).map((f) => baseName(f.path)).join(', ');
    const more = files.length > 3 ? `, +${files.length - 3} more` : '';
    return { verify: false, reason: `only non-code files changed (${names}${more})` };
  }

  if (!opts.modelWaived) {
    return { verify: true, reason: `${plural(checked.length, 'code file')} changed` };
  }

  // The agent declared the change trivial. Honor it only within a deterministic
  // guard so the marker can never wave off a risky edit. (Deletions handled above.)
  if (checked.some((f) => isConfigFile(f.path))) {
    return { verify: true, reason: 'a build/config file changed — verifying despite the trivial marker' };
  }
  if (checked.some((f) => f.created)) {
    return { verify: true, reason: 'a new code file was created — verifying despite the trivial marker' };
  }
  if (!checked.every((f) => typeof f.churn === 'number')) {
    return { verify: true, reason: 'change size unknown — verifying despite the trivial marker' };
  }
  const churn = checked.reduce((n, f) => n + (f.churn ?? 0), 0);
  if (churn > TRIVIAL_CHURN_LIMIT) {
    return { verify: true, reason: `change is too large to skip (${plural(churn, 'line')})` };
  }
  return {
    verify: false,
    reason: `agent marked it trivial (${plural(churn, 'line')} across ${plural(checked.length, 'file')})`,
  };
}

/** What a tool execution reports to the tracker (subset of the gateway's onTool). */
export interface ToolChange {
  readonly mutating: boolean;
  readonly ok: boolean;
  readonly path?: string | undefined;
  readonly deleted?: boolean | undefined;
  readonly churn?: number | undefined;
  readonly created?: boolean | undefined;
}

/**
 * Accumulates a {@link ChangeSet} from tool executions for the gateway loop,
 * which (unlike the local loop) only observes tools through a callback. Last
 * write to a path wins; a path-less mutation marks the set opaque.
 */
export interface ChangeTracker {
  /** Record one executed tool (no-op for reads and failed calls). */
  observe(t: ToolChange): void;
  /** Everything mutated since the last {@link reset}. */
  changes(): ChangeSet;
  /** True when any mutating tool succeeded since the last {@link reset}. */
  mutated(): boolean;
  /** Clear the accumulated changes (called once they've been verified). */
  reset(): void;
}

export function createChangeTracker(): ChangeTracker {
  const files = new Map<string, ChangedFile>();
  let opaque = false;
  let didMutate = false;
  return {
    observe(t) {
      if (!t.ok || !t.mutating) return;
      didMutate = true;
      if (t.path) {
        const key = t.path.replace(/\\/g, '/');
        files.set(
          key,
          mergeChange(files.get(key), {
            path: t.path,
            ...(t.churn !== undefined ? { churn: t.churn } : {}),
            ...(t.deleted ? { deleted: true } : {}),
            ...(t.created ? { created: true } : {}),
          }),
        );
      } else {
        opaque = true;
      }
    },
    changes() {
      return { files: [...files.values()], opaqueMutation: opaque };
    },
    mutated() {
      return didMutate;
    },
    reset() {
      files.clear();
      opaque = false;
      didMutate = false;
    },
  };
}
